/**
 * DriverMissionList - List of driver's missions with status filters
 */
import React, { useState, useEffect } from 'react';
import { Map, Clock, ChevronRight, Filter, AlertCircle } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import MissionStatusBadge from '../missions/MissionStatusBadge.jsx';
import { useFormat } from '../../../i18n/format.js';

const STATUS_FILTERS = [
  { id: '', label: 'Toutes' },
  { id: 'assigned', label: 'En attente' },
  { id: 'accepted', label: 'Acceptées' },
  { id: 'in_progress', label: 'En cours' },
  { id: 'completed', label: 'Terminées' },
  { id: 'cancelled', label: 'Annulées' },
];

export default function DriverMissionList({ onSelectMission }) {
  const { fmt } = useFormat();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadMissions();
  }, [statusFilter]);

  const loadMissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { _limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await driverMonoAPI.getMissions(params);
      setMissions(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter className="h-4 w-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
        {STATUS_FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              statusFilter === f.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Mission List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : missions.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
          <Map className="h-12 w-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-slate-400">
            {statusFilter ? 'Aucune mission avec ce statut' : 'Aucune mission'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {missions.map(mission => (
            <button
              key={mission.id}
              onClick={() => onSelectMission(mission.id)}
              className="w-full bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between hover:border-blue-300 dark:hover:border-blue-600 hover:shadow transition-all text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-400 dark:text-slate-500">{mission.code}</span>
                  <MissionStatusBadge status={mission.status} />
                  {mission.priority === 'urgent' && (
                    <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-medium">Urgent</span>
                  )}
                  {mission.priority === 'high' && (
                    <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs rounded font-medium">Haute</span>
                  )}
                </div>
                <p className="font-medium text-gray-900 dark:text-white truncate">{mission.title}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-slate-400">
                  {mission.planned_start && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {fmt.date(mission.planned_start, {
                        day: '2-digit', month: 'short'
                      })}
                    </span>
                  )}
                  {mission.client_name && (
                    <span className="truncate">Client: {mission.client_name}</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 dark:text-slate-500 flex-shrink-0 ml-2" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
